import React from 'react';
import BottomSheet from './ui/BottomSheet';
import { useDevice } from '../hooks/useDevice';
import { SignedIn, SignedOut, UserButton, SignInButton, SignUpButton, useUser, useClerk, useAuth } from '@clerk/clerk-react';
import ResidenceTab from './UserProfileTabs/ResidenceTab';
import PaymentsTab from './UserProfileTabs/PaymentsTab';
// CB:BEGIN fr-001-import-settings-tabs
import CoreBidsNotificationsTab from './UserProfileTabs/CoreBidsNotificationsTab';
import CoreBidsPrivacyTab from './UserProfileTabs/CoreBidsPrivacyTab';
// CB:END fr-001-import-settings-tabs
import { getCanonicalUrl, getScreenFromUrl } from '../lib/routes';

interface SharedHeaderCustomerProps {
  onNavigate: (screen: string) => void;
  showHelpButton?: boolean;
}

const SharedHeaderCustomer: React.FC<SharedHeaderCustomerProps> = ({ onNavigate, showHelpButton = false }) => {
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const { getToken } = useAuth();
  const device = useDevice();
  // CB:BEGIN fr-001-userbutton-ref
  const userButtonContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Function to open user profile modal directly (skip dropdown)
  const triggerUserButton = React.useCallback(() => {
    // First, try to find and click "Manage account" link directly if dropdown is already open
    const manageAccountLink = document.querySelector('a[href*="account"], button[aria-label*="Manage account"], [data-clerk-element="manageAccount"]') as HTMLElement;
    if (manageAccountLink) {
      manageAccountLink.click();
      return;
    }
    
    // If not found, open UserButton dropdown and then click "Manage account"
    requestAnimationFrame(() => {
      let clicked = false;
      
      // Try to find the UserButton trigger button
      if (userButtonContainerRef.current) {
        const buttons = userButtonContainerRef.current.querySelectorAll('button');
        for (const button of Array.from(buttons)) {
          if (button.disabled) continue;
          button.click();
          clicked = true;
          break;
        }
      }
      
      // Fallback: find UserButton in document
      if (!clicked) {
        const container = document.querySelector('[data-user-button-container="true"]');
        if (container) {
          const button = container.querySelector('button') as HTMLButtonElement;
          if (button) {
            button.click();
            clicked = true;
          }
        }
      }
      
      // After opening dropdown, wait a moment then click "Manage account"
      if (clicked) {
        setTimeout(() => {
          // Try multiple selectors for "Manage account" link
          const selectors = [
            'a[href*="account"]',
            'button[aria-label*="Manage account"]',
            '[data-clerk-element="manageAccount"]',
            'a:has-text("Manage account")',
            '*:contains("Manage account")',
          ];
          
          for (const selector of selectors) {
            try {
              const link = document.querySelector(selector) as HTMLElement;
              if (link && link.offsetParent !== null) { // Check if visible
                link.click();
                return;
              }
            } catch (e) {
              // Some selectors might not be valid, continue
            }
          }
          
          // Fallback: try to find any link/button with "Manage" or "account" text
          const allLinks = document.querySelectorAll('a, button');
          for (const link of Array.from(allLinks)) {
            const text = link.textContent?.toLowerCase() || '';
            if ((text.includes('manage') || text.includes('account')) && link.offsetParent !== null) {
              (link as HTMLElement).click();
              return;
            }
          }
        }, 100); // Small delay to let dropdown render
      } else {
        // If we couldn't open dropdown, try openUserProfile as fallback
        openUserProfile();
      }
    });
  }, [openUserProfile]);
  // CB:END fr-001-userbutton-ref
  // CB:BEGIN escrow-button-state
  const [hasEscrowAccount, setHasEscrowAccount] = React.useState(false);
  const [checkingEscrow, setCheckingEscrow] = React.useState(true);
  // CB:END escrow-button-state

  // CB:BEGIN escrow-account-check
  // Check if active project has an escrow account
  React.useEffect(() => {
    const checkEscrowAccount = async () => {
      try {
        const projectId = localStorage.getItem('active_project_id');
        if (!projectId) {
          setHasEscrowAccount(false);
          setCheckingEscrow(false);
          return;
        }

        // First check if project has an accepted bid (escrow only exists after bid acceptance)
        // This prevents unnecessary 404 requests for projects without accepted bids
        try {
          const { getApiUrl } = await import('../lib/getApiUrl');
          const token = await getToken();
          const projectResponse = await fetch(getApiUrl(`/api/projects/${projectId}`), {
            credentials: 'include',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          
          if (!projectResponse.ok) {
            // Stale / forbidden project ID — skip escrow check entirely
            setHasEscrowAccount(false);
            setCheckingEscrow(false);
            return;
          }
          const projectData = await projectResponse.json();
          const project = projectData.project || projectData;
          const bids = project.bids || [];
          const hasAcceptedBid = bids.some((bid: any) => bid.isAccepted === true);

          // Only check for escrow if project has accepted bid
          if (!hasAcceptedBid) {
            setHasEscrowAccount(false);
            setCheckingEscrow(false);
            return;
          }
        } catch (projectError) {
          // Network error checking project — skip escrow check
          setHasEscrowAccount(false);
          setCheckingEscrow(false);
          return;
        }

        // Check for escrow account (only if project has accepted bid)
        const { getApiUrl } = await import('../lib/getApiUrl');
        const token = await getToken();
        const escrowResponse = await fetch(getApiUrl(`/api/escrow/${projectId}/dashboard`), {
          credentials: 'include',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (escrowResponse.ok) {
          const escrowData = await escrowResponse.json();
          const escrow = escrowData.escrowAccount || escrowData;
          const totalDeposited = escrow?.totalDeposited ?? escrow?.totalAmountCents ?? 0;
          const isFunded = escrow?.status === 'FUNDED' || (typeof totalDeposited === 'number' && totalDeposited > 0);
          setHasEscrowAccount(!!escrow && !!isFunded);
        } else if (escrowResponse.status === 404) {
          // 404 is expected when no escrow account exists yet - don't log as error
          setHasEscrowAccount(false);
        } else {
          // Other errors (500, etc.) - log but don't treat as error
          setHasEscrowAccount(false);
        }
      } catch (error) {
        // Network errors - don't log as warning, just set to false
        setHasEscrowAccount(false);
      } finally {
        setCheckingEscrow(false);
      }
    };

    checkEscrowAccount();

    // Listen for project changes
    const handleStorageChange = () => {
      checkEscrowAccount();
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for escrow account creation events
    const handleEscrowCreated = () => {
      checkEscrowAccount();
    };
    window.addEventListener('cb:escrow-created', handleEscrowCreated);
    window.addEventListener('cb:payment-succeeded', handleEscrowCreated);
    
    // Also check when active_project_id changes in same tab
    const checkInterval = setInterval(() => {
      const currentProjectId = localStorage.getItem('active_project_id');
      if (currentProjectId) {
        checkEscrowAccount();
      }
    }, 5000); // Check every 5 seconds

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cb:escrow-created', handleEscrowCreated);
      window.removeEventListener('cb:payment-succeeded', handleEscrowCreated);
      clearInterval(checkInterval);
    };
  }, []);
  // CB:END escrow-account-check

  const handleNavigation = (screen: string) => {
    try {
      onNavigate?.(screen);
    } catch (err) {
      console.warn('Navigation handler threw, falling back to hard navigation', err);
    }

    // Fallback: sync URL after parent onNavigate — use microtask + rAF (no fixed 50ms delay).
    // CB:BEGIN header-nav-url-sync
    const syncUrlAfterNav = () => {
      const current =
        getScreenFromUrl(window.location.pathname) ||
        (window.history.state && window.history.state.screen);

      if (current === screen) return;

      const canonical = getCanonicalUrl(screen) || `/?screen=${screen}`;
      window.history.pushState({ screen }, '', canonical);
      window.dispatchEvent(new PopStateEvent('popstate', { state: { screen } }));
    };
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        syncUrlAfterNav();
      });
    });
    // CB:END header-nav-url-sync
  };

  // CB:BEGIN homeowner-help-navigate-helper
  const openHelp = () => {
    try { sessionStorage.setItem('cb.help.origin', 'customer-dashboard'); } catch {}
    handleNavigation('homeowner-help');
  };
  // CB:END homeowner-help-navigate-helper

  // Create the customer navigation buttons
  const customerNavButtons = (
    <>
      {/* CB:BEGIN homeowner-header-remove-home-newproject */}
      {/* Removed Home and New Project buttons for homeowner header */}
      {/* CB:END homeowner-header-remove-home-newproject */}
      
      {/* Home Button */}
      <button
        onClick={() => handleNavigation('customer-dashboard')}
        style={{
          background: '#6b7280',
          border: 'none',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          padding: '8px 16px',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#6b7280';
        }}
      >
        Home
      </button>
      
      {/* CB:BEGIN documents-button */}
      <button
        onClick={() => handleNavigation('documents')}
        style={{
          background: '#6b7280',
          border: 'none',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          padding: '8px 16px',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#6b7280';
        }}
      >
        Documents
      </button>
      {/* CB:END documents-button */}

      {/* CB:BEGIN escrow-button */}
      <button
        onClick={() => hasEscrowAccount && handleNavigation('escrow-dashboard')}
        disabled={!hasEscrowAccount || checkingEscrow}
        style={{
          background: hasEscrowAccount ? '#059669' : '#9ca3af',
          border: 'none',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          padding: '8px 16px',
          borderRadius: '6px',
          cursor: hasEscrowAccount ? 'pointer' : 'not-allowed',
          opacity: hasEscrowAccount ? 1 : 0.6,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (hasEscrowAccount) {
            e.currentTarget.style.background = '#047857';
          }
        }}
        onMouseLeave={(e) => {
          if (hasEscrowAccount) {
            e.currentTarget.style.background = '#059669';
          } else {
            e.currentTarget.style.background = '#9ca3af';
          }
        }}
        title={hasEscrowAccount ? 'View escrow' : 'Escrow is available after you fund the project (complete payment)'}
      >
        Escrow
      </button>
      {/* CB:END escrow-button */}
      
      {/* CB:BEGIN enhancify-financing-button */}
      <button 
        className="btn btn-secondary" 
        style={{ 
          fontSize: '14px', 
          padding: '8px 16px', 
          color: 'white',
          background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
          border: 'none',
          fontWeight: '600',
          boxShadow: '0 2px 8px rgba(217, 119, 6, 0.3)',
          cursor: 'pointer'
        }}
        onClick={(e) => {
          e.preventDefault();
          // Open financing page in a new tab
          const financingUrl = `${window.location.origin}/financing`;
          window.open(financingUrl, '_blank', 'noopener,noreferrer');
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #b45309 0%, #92400e 100%)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 119, 6, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #d97706 0%, #b45309 100%)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 119, 6, 0.3)';
        }}
      >
        Financing Options here
      </button>
      {/* CB:END enhancify-financing-button */}
      
      {/* CB:BEGIN settings-button */}
      <button
        className="btn btn-secondary"
        style={{
          fontSize: '14px',
          padding: '8px 16px',
          color: 'white',
          background: '#6b7280',
          border: 'none',
          fontWeight: '500',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={triggerUserButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#6b7280';
        }}
      >
        Settings
      </button>
      {/* CB:END settings-button */}

      {/* CB:BEGIN contact-us-button */}
      <a
        href={`mailto:support@corebids.com?subject=Project%20${encodeURIComponent(localStorage.getItem('active_project_id') || '')}`}
        className="btn btn-secondary"
        style={{
          fontSize: '14px',
          padding: '8px 16px',
          color: 'white',
          background: '#6b7280',
          border: 'none',
          fontWeight: '500',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          textDecoration: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#4b5563';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#6b7280';
        }}
      >
        Contact Us
      </a>
      {/* CB:END contact-us-button */}

      {/* CB:BEGIN homeowner-help-button */}
      {showHelpButton && (
        <button
          onClick={() => openHelp()}
          style={{
            background: '#0284c7',
            border: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#0369a1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#0284c7';
          }}
        >
          Help
        </button>
      )}
      {/* CB:END homeowner-help-button */}
    </>
  );

  const [menuOpen, setMenuOpen] = React.useState(false);
  const { isMobileLayout } = useDevice();

  return (
          <div className="header sticky top-0 z-40" style={{ background: '#123c3b', color: 'white', padding: isMobileLayout ? '12px 16px' : '20px 30px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 40, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
      {/* CB:BEGIN safe-area-top-spacer */}
      <div className="sr-only" style={{ paddingTop: 'env(safe-area-inset-top)' }} />
      {/* CB:END safe-area-top-spacer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobileLayout ? 12 : 24, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
        <div className="logo" style={{ fontSize: isMobileLayout ? 20 : 24, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }} onClick={() => handleNavigation('landing')}>CoreBids</div>
        {/* Customer Navigation Buttons - only shown when logged in */}
        <SignedIn>
          {!isMobileLayout && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              {customerNavButtons}
            </div>
          )}
        </SignedIn>
      </div>
      
      {!isMobileLayout && (
      <div className="nav-buttons" style={{ display: 'flex', gap: 15, alignItems: 'center', flexShrink: 0 }}>
        <SignedIn>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>Welcome, {user?.firstName || user?.fullName?.split(' ')[0] || 'User'}</span>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper */}
          <div ref={userButtonContainerRef} data-user-button-container="true" style={{ display: 'inline-block' }}>
          {/* CB:END fr-001-userbutton-ref-wrapper */}
          <UserButton afterSignOutUrl="/" userProfileMode="modal">
            <UserButton.UserProfilePage label="Residence" url="residence" labelIcon={<span>🏠</span>}>
              <ResidenceTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="Payments" url="payments" labelIcon={<span>💳</span>}>
              <PaymentsTab />
            </UserButton.UserProfilePage>
            {/* CB:BEGIN fr-001-corebids-settings-pages */}
            <UserButton.UserProfilePage label="CoreBids Notifications" url="corebids-notifications" labelIcon={<span>🔔</span>}>
              <CoreBidsNotificationsTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="CoreBids Privacy" url="corebids-privacy" labelIcon={<span>🔒</span>}>
              <CoreBidsPrivacyTab />
            </UserButton.UserProfilePage>
            {/* CB:END fr-001-corebids-settings-pages */}
          </UserButton>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper-close */}
          </div>
          {/* CB:END fr-001-userbutton-ref-wrapper-close */}
        </SignedIn>
        {/* CB:BEGIN header-mobile-sizes */}
        <SignedOut>
          <SignInButton mode="modal">
            <button className="btn btn-secondary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Login</button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="btn btn-primary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Sign Up</button>
          </SignUpButton>
        </SignedOut>
        {/* CB:END header-mobile-sizes */}
      </div>
      )}
      {/* Mobile hamburger */}
      {device.isMobileLayout && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button aria-label="Menu" onClick={() => setMenuOpen(true)} className="btn btn-secondary" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
            ☰
          </button>
        </div>
      )}

      {isMobileLayout && (
      <BottomSheet isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Menu">
        <div style={{ display: 'grid', gap: 12 }}>
          <SignedIn>
            <div style={{ display: 'grid', gap: 8 }}>
              {customerNavButtons}
            </div>
          </SignedIn>
          {/* CB:BEGIN header-mobile-sizes-2 */}
          <SignedOut>
            <SignInButton mode="modal">
              <button className="btn btn-secondary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Login</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="btn btn-primary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Sign Up</button>
            </SignUpButton>
          </SignedOut>
          {/* CB:END header-mobile-sizes-2 */}
        </div>
      </BottomSheet>
      )}
    </div>
  );
};

export default SharedHeaderCustomer; 